// Super Admin → Intro beheer.
// Per standaard pagina kan de admin hier de default inleiding instellen die
// nieuwe merken automatisch meekrijgen bij aanmaak vanuit het dashboard-
// overzicht. Bestaande merken behouden hun eigen (eventueel overschreven)
// intro — dit is puur de seed-waarde voor nieuwe omgevingen.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Header } from "./WorkspacesTab";
import { useGlobalConfig } from "@/contexts/GlobalConfigContext";

export default function PageIntrosTab() {
  const { refresh: refreshGlobal } = useGlobalConfig();
  const [intros, setIntros] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/config");
        setIntros(data.page_intros || {});
        setInitial(data.page_intros || {});
      } catch { toast.error("Kon intros niet laden"); }
    })();
  }, []);

  const dirty = useMemo(() => {
    if (!intros || !initial) return false;
    const keys = new Set([...Object.keys(intros), ...Object.keys(initial)]);
    for (const k of keys) {
      if ((intros[k] || "") !== (initial[k] || "")) return true;
    }
    return false;
  }, [intros, initial]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/super-admin/config", { page_intros: intros });
      toast.success("Opgeslagen — nieuwe omgevingen krijgen deze inleidingen");
      setInitial(intros);
      await refreshGlobal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (!intros) return <div className="text-sm text-gray-500">Laden…</div>;

  const names = Object.keys(intros);

  return (
    <div data-testid="sa-page-intros">
      <Header
        title="Intro beheer"
        sub="Standaard inleiding per pagina. Deze wordt gebruikt bij nieuwe omgevingen. Gebruikers kunnen hem later in hun eigen hub overschrijven."
      />

      <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6 mb-6">
        <div className="space-y-5">
          {names.map((name) => (
            <IntroRow
              key={name}
              name={name}
              value={intros[name] || ""}
              onChange={(v) => setIntros((s) => ({ ...s, [name]: v }))}
            />
          ))}
          {names.length === 0 && (
            <div className="text-sm text-gray-500">Geen standaard pagina's gevonden.</div>
          )}
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="sa-page-intros-save"
          className="h-10 px-6 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
        >{saving ? "Opslaan…" : "Opslaan"}</Button>
      </div>
    </div>
  );
}

function IntroRow({ name, value, onChange }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1.5">{name}</div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Inleiding voor deze pagina — laat leeg om geen intro te seeden."
        className="w-full bg-white border border-gray-200 rounded-[var(--global-radius)] p-3 text-sm leading-relaxed focus:outline-none"
        data-testid={`sa-intro-${name.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </div>
  );
}
